import React, { useEffect, useState } from 'react';
import { Card, Grid, Typography, IconButton, Box } from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { IPatient } from '../../../constants/types';
import { fetchPatient } from '../../../db';
import { dateToFullDateString, getPatientID, getHealthIDLabel } from '../../../utils/index.jsx';
import Loader from '../../../components/Loader';

interface PatientSelectionBannerProps extends LocalizeContextProps {
    uuidPatient: string;
    onRemovePatient: () => void;
}

const PatientSelectionBanner: React.FC<PatientSelectionBannerProps> = ({ 
    uuidPatient, 
    onRemovePatient, 
    activeLanguage 
}) => {
    const [patient, setPatient] = useState<IPatient | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPatient = async () => {
            try {
                setLoading(true);
                const fetchedPatient = await fetchPatient(uuidPatient);
                setPatient(fetchedPatient);
            } catch (error) {
                console.error('Error loading patient:', error);
            } finally {
                setLoading(false);
            }
        };

        if (uuidPatient) {
            loadPatient();
        }
    }, [uuidPatient]);

    if (loading) {
        return (
            <Card sx={{ 
                mb: 2, 
                backgroundColor: '#e3f2fd', 
                border: '2px solid #1976d2',
                borderRadius: 2
            }}>
                <Box p={2} display="flex" alignItems="center">
                    <Loader />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        <Translate id="general.loading" />...
                    </Typography>
                </Box>
            </Card>
        );
    }

    if (!patient || !patient.personalData) {
        return null;
    }

    return (
        <Card sx={{ 
            mb: 2, 
            backgroundColor: '#e8f5e8', 
            border: '2px solid #4caf50',
            borderRadius: 2,
            boxShadow: 3
        }}>
            <Box p={2}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                        <PersonIcon sx={{ color: '#4caf50', fontSize: 32 }} />
                    </Grid>
                    <Grid item xs>
                        <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                            <Translate id="pages.hospital.outpatients.creating_appointment_for" />
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {patient.personalData.name} {patient.personalData.surnames}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <span style={{ fontWeight: 'bold' }}>
                                        <Translate id={getHealthIDLabel(patient)} />:
                                    </span> {getPatientID(patient)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    <span style={{ fontWeight: 'bold' }}>
                                        <Translate id="investigation.create.personal_data.fields.birthdate" />:
                                    </span> {dateToFullDateString(patient.personalData.birthdate, activeLanguage.code)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <span style={{ fontWeight: 'bold' }}>
                                        <Translate id="investigation.create.personal_data.fields.sex" />:
                                    </span> {patient.personalData.sex}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <IconButton 
                            onClick={onRemovePatient}
                            sx={{ 
                                color: '#d32f2f',
                                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.2)',
                                }
                            }}
                            title="Remove patient selection"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
};

export default withLocalize(PatientSelectionBanner);
